import {
  LIST_DEACTIVATED,
  LIST_IN_USE,
  LIST_MISSING,
  LIST_NOT_FOUND,
  RULE_AMBIGUOUS,
  RULE_MISSING,
  RULE_NOT_FOUND,
  RULE_OUT_OF_WINDOW,
} from '../pricingRoutes';

/**
 * # The client owns every sentence
 *
 * A manager pressed Start on a PlayStation and was shown this:
 *
 * > Kids #01 has no pricing rule... Add one at
 * > POST /resources/cmt64plmr000t07625sbqhgyr/pricing-rules before starting the
 * > timer.
 *
 * An HTTP verb, a route and a cuid, handed to someone holding a crying child —
 * and it would have reached an Arabic reader in English, because a string that
 * arrives over the wire cannot be translated by the browser that receives it.
 *
 * The API's sentences are clean now. **That is not the fix.** A server sentence
 * is written once, in one language, by someone who cannot see the screen it will
 * land on; the day one of them says something operational again, the dashboard
 * repeats it. So nothing here ever renders {@link Refusal.serverMessage}. Every
 * word a venue reads comes out of `en.json` / `ar.json`, chosen by `code` and
 * filled from the machine-readable detail keys the API deliberately puts beside
 * the message.
 *
 * A code this file does not know gets the generic sentence — {@link GENERIC} —
 * and never the server's text. That is the case people argue about, so: an
 * untranslated English sentence naming a JSON field is not more informative than
 * "That did not work", it is differently useless *and* it leaks the shape of the
 * API to a guest-facing terminal. The server's own words go where a developer
 * can find them and a venue cannot — the console, and a `data-` attribute on the
 * alert — which is what {@link reportRefusal} is for.
 *
 * ## Three refusals, three fixes
 *
 * The single most expensive thing this file gets right is that a start refused
 * for want of a price is not one failure. It is three, fixed in three different
 * places:
 *
 *  - no card is attached → attach one, **on the resource**;
 *  - a card is attached and has no rate in force → add or reactivate a rate,
 *    **on the card**;
 *  - rates exist and none covers this moment → widen a window, **on the card**.
 *
 * A banner that offered one button for all three would send a manager hunting a
 * rate card for a gap that is not there, or building a second card beside one
 * that only needed a switch flipped. {@link refusalSentence} returns which of
 * the three it is, so the button can lead somewhere that helps.
 */

/**
 * A refusal body, read off an axios failure.
 *
 * Every field but `code` and `serverMessage` is optional because a 400 from the
 * global `ValidationPipe` carries none of them — and arrives with `message` as
 * an array of strings rather than one.
 */
export interface Refusal {
  /** The API's machine-readable code, or `null` when the body carried none. */
  code: string | null;
  /**
   * What the server actually said.
   *
   * NEVER RENDERED. It exists so a developer looking at the console or at the
   * alert's `data-server-message` can see the real reason, and so a bug report
   * can quote it. Anything that puts this on screen has reintroduced the bug at
   * the top of this file.
   */
  serverMessage: string;
  /** The HTTP status, where there was a response at all. */
  status?: number;

  /** Session-start refusals — how this resource is configured right now. */
  resourceId?: string;
  pricingListId?: string | null;
  pricingListName?: string;
  ruleCount?: number;
  activeRuleCount?: number;

  /** `RULE_AMBIGUOUS` — the rule already in force that a draft would collide with. */
  conflictsWithName?: string;
  priority?: number;

  /** `LIST_IN_USE` — what still charges from the card someone tried to retire. */
  attachedResourceCount?: number;
  attachedResourceNames?: string[];
}

const ROOT = 'spaces.pricingRules';

/** The sentence for a code nothing here recognises. Never the server's own. */
export const GENERIC = {
  titleKey: `${ROOT}.refusal.genericTitle`,
  bodyKey: `${ROOT}.refusal.genericBody`,
} as const;

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

function count(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * Reads a refusal off an axios failure.
 *
 * Always answers — a network failure with no response at all is a refusal with
 * no code, which is exactly the case {@link GENERIC} exists for. The caller
 * never has to decide whether there is something to render.
 */
export function refusalOf(error: unknown): Refusal {
  const response = (error as { response?: { status?: number; data?: Record<string, unknown> } })
    .response;
  const data = response?.data;
  const raw = data?.message;
  const serverMessage = Array.isArray(raw)
    ? raw.join(', ')
    : (text(raw) ?? text((error as Error | undefined)?.message) ?? '');

  const refusal: Refusal = {
    code: text(data?.code) ?? null,
    serverMessage,
    status: response?.status,
    resourceId: text(data?.resourceId),
    pricingListId: text(data?.pricingListId) ?? (data?.pricingListId === null ? null : undefined),
    pricingListName: text(data?.pricingListName),
    ruleCount: count(data?.ruleCount),
    activeRuleCount: count(data?.activeRuleCount),
    conflictsWithName: text(data?.conflictsWithName),
    priority: count(data?.priority),
    attachedResourceCount: count(data?.attachedResourceCount),
    attachedResourceNames: Array.isArray(data?.attachedResourceNames)
      ? (data.attachedResourceNames as unknown[]).filter(
          (name): name is string => typeof name === 'string',
        )
      : undefined,
  };

  reportRefusal(refusal);
  return refusal;
}

/**
 * The server's own words, where only a developer will meet them.
 *
 * Guarded on `DEV` so a venue's browser console stays empty in production — a
 * console that prints route names on every refusal is a slower version of the
 * same leak.
 */
export function reportRefusal(refusal: Refusal): void {
  if (!import.meta.env.DEV) return;
  // eslint-disable-next-line no-console
  console.debug('[pricing] refused', refusal.code ?? '(no code)', refusal.serverMessage, refusal);
}

/**
 * Whether a failed session start was refused for want of a price.
 *
 * All three codes, including the one that did not exist while rules hung off a
 * resource: a PlayStation created before there were any cards is attached to
 * nothing, which is the state most venues will meet first.
 */
export function isPricingRefusal(refusal: Refusal): boolean {
  return (
    refusal.code === LIST_MISSING ||
    refusal.code === RULE_MISSING ||
    refusal.code === RULE_OUT_OF_WINDOW
  );
}

/**
 * Where the button on a refusal should lead.
 *
 * `attachList` is the resource's own form, because `pricingListId` is a field on
 * the resource and nowhere else. `openList` is the rate card, and it carries
 * WHICH of the two card-shaped mistakes was made — the copy on the button
 * differs, and so does what the manager is looking for once they arrive.
 */
export type RefusalFix =
  | { kind: 'attachList'; resourceId?: string }
  | { kind: 'openList'; listId: string | null; intent: 'addRule' | 'widenWindow' | 'moveResources' }
  | null;

export interface RefusalSentence {
  titleKey: string;
  titleParams?: Record<string, string | number>;
  bodyKey: string;
  bodyParams?: Record<string, string | number>;
  /** The button's label, or `null` where the fix is the form already on screen. */
  fixKey: string | null;
  fix: RefusalFix;
}

/**
 * The names a sentence can put in front of a person.
 *
 * Names, never ids: `pricingListName` is what the venue typed on the card and
 * `resource` is what it typed on the console, and neither is a cuid. That is the
 * rule the whole file turns on — a sentence that interpolated `pricingListId`
 * would be the original bug with a nicer font.
 */
export interface RefusalNames {
  /** The resource the operator is standing at. */
  resource: string;
  /** The rate card, already fallen back to a localized "the price list". */
  list: string;
  /**
   * The thing being saved when the refusal came back — the draft rule's name.
   *
   * Only the ambiguity sentence needs it, and it needs it because "Peak and
   * Base would both apply" is unreadable without both halves, and the server
   * only sends the half the venue could not already see on its own screen.
   */
  subject?: string;
  /**
   * The rule a draft collides with, already fallen back to a localized
   * "another rule" — the 409 carries `conflictsWithName`, but a body that
   * arrives without it must still make a sentence.
   */
  otherRule?: string;
}

/** One refusal, as a sentence the venue can read and act on. */
export function refusalSentence(refusal: Refusal, names: RefusalNames): RefusalSentence {
  const { resource, list } = names;

  switch (refusal.code) {
    case LIST_MISSING:
      return {
        titleKey: `${ROOT}.refusal.missingTitle`,
        titleParams: { name: resource },
        bodyKey: `${ROOT}.refusal.listMissingBody`,
        bodyParams: { name: resource },
        fixKey: `${ROOT}.refusal.attachFix`,
        fix: { kind: 'attachList', resourceId: refusal.resourceId },
      };

    case RULE_MISSING: {
      // `ruleCount` rides along on the payload precisely so a dashboard can tell
      // "this card was never filled in" from "somebody switched all of it off"
      // without parsing English. They are one click apart and read nothing alike.
      const total = refusal.ruleCount ?? 0;
      return {
        titleKey: `${ROOT}.refusal.missingTitle`,
        titleParams: { name: resource },
        bodyKey: total === 0 ? `${ROOT}.refusal.missingNone` : `${ROOT}.refusal.missingAllOff`,
        bodyParams: { count: total, list },
        fixKey: `${ROOT}.refusal.addRuleFix`,
        fix: { kind: 'openList', listId: refusal.pricingListId ?? null, intent: 'addRule' },
      };
    }

    case RULE_OUT_OF_WINDOW:
      return {
        titleKey: `${ROOT}.refusal.outOfWindowTitle`,
        titleParams: { name: resource },
        bodyKey: `${ROOT}.refusal.outOfWindowBody`,
        bodyParams: { count: refusal.activeRuleCount ?? 0, list },
        fixKey: `${ROOT}.refusal.widenFix`,
        fix: { kind: 'openList', listId: refusal.pricingListId ?? null, intent: 'widenWindow' },
      };

    case RULE_AMBIGUOUS:
      // No button: the manager is already in the form that caused it, and the
      // fix is one field on the draft in front of them.
      return {
        titleKey: `${ROOT}.ambiguousTitle`,
        bodyKey: `${ROOT}.ambiguous`,
        bodyParams: {
          name: names.subject ?? '',
          other: refusal.conflictsWithName || names.otherRule || '',
          priority: refusal.priority ?? 0,
        },
        fixKey: null,
        fix: null,
      };

    case LIST_IN_USE:
      return {
        titleKey: `${ROOT}.lists.inUseTitle`,
        titleParams: { name: list },
        bodyKey: `${ROOT}.lists.inUseBody`,
        bodyParams: {
          count: refusal.attachedResourceCount ?? 0,
          // Up to five names, joined here rather than rendered as a list,
          // because it belongs inside the sentence that explains what to move.
          resources: (refusal.attachedResourceNames ?? []).join(', '),
        },
        fixKey: null,
        fix: null,
      };

    case LIST_DEACTIVATED:
      return {
        titleKey: `${ROOT}.lists.deactivatedTitle`,
        titleParams: { name: list },
        bodyKey: `${ROOT}.lists.deactivatedBody`,
        fixKey: `${ROOT}.manage`,
        fix: { kind: 'openList', listId: refusal.pricingListId ?? null, intent: 'moveResources' },
      };

    case LIST_NOT_FOUND:
      return {
        titleKey: `${ROOT}.lists.goneTitle`,
        bodyKey: `${ROOT}.lists.goneBody`,
        fixKey: null,
        fix: null,
      };

    case RULE_NOT_FOUND:
      return {
        titleKey: `${ROOT}.refusal.ruleGoneTitle`,
        bodyKey: `${ROOT}.refusal.ruleGoneBody`,
        fixKey: null,
        fix: null,
      };

    default:
      // Including `code: null` — a 500, a dropped connection, a validation error
      // from the global pipe. The venue is told the save did not happen, in
      // their own language, and the server's words are in the console.
      return { titleKey: GENERIC.titleKey, bodyKey: GENERIC.bodyKey, fixKey: null, fix: null };
  }
}
